/**
 * Dashboard module
 * Handles the Dashboard tab functionality
 */

import { API } from '../core/api.js';
import { startServicesHealthCheck } from './health.js';
import { loadBatches } from './batches.js';

/**
 * Load dashboard
 */
export async function loadDashboard() {
    loadWorkerDashboard();
    loadBatches();
    loadRecentActivity();
    startServicesHealthCheck();
}

/**
 * Load worker status for dashboard
 */
export async function loadWorkerDashboard() {
    try {
        const status = await API.get('/api/workers/status');

        const container = document.getElementById('worker-dashboard');
        if (!container) return;

        container.innerHTML = `
            <div class="worker-card">
                <div class="worker-header">
                    <div class="worker-title">📥 Download Worker</div>
                    <span class="badge ${status.download_worker.status === 'running' ? 'downloaded' : 'pending'}">
                        ${status.download_worker.status}
                    </span>
                </div>
                <div class="worker-stats">
                    <div class="worker-stat">
                        <div class="worker-stat-value">${status.download_worker.episodes_downloaded}</div>
                        <div class="worker-stat-label">Downloaded</div>
                    </div>
                    <div class="worker-stat">
                        <div class="worker-stat-value">${status.download_worker.episodes_failed}</div>
                        <div class="worker-stat-label">Failed</div>
                    </div>
                </div>
                ${status.download_worker.current_task ? `<p style="margin-top: 1rem; color: var(--text-secondary);">Current: ${status.download_worker.current_task}</p>` : ''}
            </div>

            <div class="worker-card">
                <div class="worker-header">
                    <div class="worker-title">⚡ Processing Worker</div>
                    <span class="badge ${status.process_worker.status === 'running' ? 'downloaded' : 'pending'}">
                        ${status.process_worker.status}
                    </span>
                </div>
                <div class="worker-stats">
                    <div class="worker-stat">
                        <div class="worker-stat-value">${status.process_worker.episodes_processed}</div>
                        <div class="worker-stat-label">Processed</div>
                    </div>
                    <div class="worker-stat">
                        <div class="worker-stat-value">${status.process_worker.episodes_failed}</div>
                        <div class="worker-stat-label">Failed</div>
                    </div>
                </div>
                ${status.process_worker.current_task ? `<p style="margin-top: 1rem; color: var(--text-secondary);">Current: ${status.process_worker.current_task}</p>` : ''}
            </div>
        `;
    } catch (error) {
        console.error('Error loading worker dashboard:', error);
    }
}

/**
 * Load recent activity for dashboard
 */
export async function loadRecentActivity() {
    try {
        const data = await API.get('/api/podcasts?limit=3');
        const podcasts = data.podcasts || [];

        const container = document.getElementById('recent-activity');
        if (!container) return;

        container.innerHTML = '<p style="color: var(--text-secondary);">Loading recent episodes...</p>';

        // Get recent episodes from first few podcasts
        let allEpisodes = [];
        for (const podcast of podcasts) {
            const episodesData = await API.get(`/api/podcasts/${podcast.id}/episodes`);
            // The response is an object with an 'episodes' property
            if (episodesData.episodes && Array.isArray(episodesData.episodes)) {
                episodesData.episodes.forEach(ep => {
                    ep.podcast_title = podcast.title;
                    allEpisodes.push(ep);
                });
            }
        }

        // Sort by date and take recent ones
        allEpisodes.sort((a, b) => new Date(b.published_date) - new Date(a.published_date));
        const recentEpisodes = allEpisodes.slice(0, 5);

        if (recentEpisodes.length === 0) {
            container.innerHTML = '<p style="color: var(--text-secondary);">No episodes found. Add some podcasts to get started!</p>';
            return;
        }

        container.innerHTML = recentEpisodes.map(ep => `
            <div class="episode-item">
                <div class="episode-header">
                    <div>
                        <div class="episode-title">${ep.title}</div>
                        <div style="color: var(--text-secondary); font-size: 0.85rem;">${ep.podcast_title}</div>
                    </div>
                    <div class="episode-actions">
                        ${!ep.downloaded ? `<button class="btn btn-small" onclick="downloadEpisode(${ep.id})">Download</button>` : ''}
                        ${ep.downloaded && !ep.processed ? `<button class="btn btn-small" onclick="processEpisode(${ep.id})">Process</button>` : ''}
                        <button class="btn btn-small btn-secondary" onclick="viewEpisodeDetails(${ep.id})">View</button>
                    </div>
                </div>
                <div class="episode-meta">
                    <span>${ep.published_date ? new Date(ep.published_date).toLocaleDateString() : 'Unknown date'}</span>
                    ${ep.duration_seconds ? `<span>${Math.floor(ep.duration_seconds / 60)} min</span>` : ''}
                    ${ep.downloaded ? '<span class="badge downloaded">Downloaded</span>' : ''}
                    ${ep.processed ? '<span class="badge processed">Processed</span>' : ''}
                </div>
            </div>
        `).join('');
    } catch (error) {
        console.error('Error loading recent activity:', error);
    }
}

/**
 * Load stats for header and dashboard
 */
export async function loadStats() {
    try {
        const stats = await API.get('/api/stats');

        // Header stats
        const statPodcasts = document.getElementById('stat-podcasts');
        const statEpisodes = document.getElementById('stat-episodes');
        const statDownloaded = document.getElementById('stat-downloaded');
        const statProcessed = document.getElementById('stat-processed');
        const statProcessing = document.getElementById('stat-processing');
        const statFailed = document.getElementById('stat-failed');

        if (statPodcasts) statPodcasts.textContent = stats.podcasts;
        if (statEpisodes) statEpisodes.textContent = stats.episodes.total;
        if (statDownloaded) statDownloaded.textContent = stats.episodes.downloaded;
        if (statProcessed) statProcessed.textContent = stats.episodes.processed;
        if (statProcessing) statProcessing.textContent = stats.transcription.processing;
        if (statFailed) statFailed.textContent = stats.transcription.failed;

        // Dashboard detailed stats
        const dashboardTotal = document.getElementById('dashboard-total');
        if (dashboardTotal) {
            dashboardTotal.textContent = stats.episodes.total;

            const dashboardDownloaded = document.getElementById('dashboard-downloaded');
            const dashboardPendingDownload = document.getElementById('dashboard-pending-download');
            const dashboardDownloadedNotProcessed = document.getElementById('dashboard-downloaded-not-processed');

            if (dashboardDownloaded) dashboardDownloaded.textContent = stats.episodes.downloaded;
            if (dashboardPendingDownload) dashboardPendingDownload.textContent = stats.episodes.pending_download;
            if (dashboardDownloadedNotProcessed) dashboardDownloadedNotProcessed.textContent = stats.episodes.downloaded_not_processed;

            const dashboardTranscriptionCompleted = document.getElementById('dashboard-transcription-completed');
            const dashboardTranscriptionProcessing = document.getElementById('dashboard-transcription-processing');
            const dashboardTranscriptionPending = document.getElementById('dashboard-transcription-pending');
            const dashboardTranscriptionFailed = document.getElementById('dashboard-transcription-failed');
            const dashboardTranscriptionNotAvailable = document.getElementById('dashboard-transcription-not-available');

            if (dashboardTranscriptionCompleted) dashboardTranscriptionCompleted.textContent = stats.transcription.completed;
            if (dashboardTranscriptionProcessing) dashboardTranscriptionProcessing.textContent = stats.transcription.processing;
            if (dashboardTranscriptionPending) dashboardTranscriptionPending.textContent = stats.transcription.pending;
            if (dashboardTranscriptionFailed) dashboardTranscriptionFailed.textContent = stats.transcription.failed;
            if (dashboardTranscriptionNotAvailable) dashboardTranscriptionNotAvailable.textContent = stats.transcription.not_available;

            // LLM Analytics stats
            const dashboardLLMCompleted = document.getElementById('dashboard-llm-completed');
            const dashboardLLMProcessing = document.getElementById('dashboard-llm-processing');
            const dashboardLLMPending = document.getElementById('dashboard-llm-pending');
            const dashboardLLMFailed = document.getElementById('dashboard-llm-failed');
            const dashboardLLMAnalyticsReady = document.getElementById('dashboard-llm-analytics-ready');

            if (stats.llm) {
                if (dashboardLLMCompleted) dashboardLLMCompleted.textContent = stats.llm.completed;
                if (dashboardLLMProcessing) dashboardLLMProcessing.textContent = stats.llm.processing;
                if (dashboardLLMPending) dashboardLLMPending.textContent = stats.llm.pending;
                if (dashboardLLMFailed) dashboardLLMFailed.textContent = stats.llm.failed;
                if (dashboardLLMAnalyticsReady) dashboardLLMAnalyticsReady.textContent = stats.llm.analytics_ready;
            }

            // Update stuck counts in workers tab
            const transcriptionStuckCount = document.getElementById('transcription-stuck-count');
            const llmStuckCount = document.getElementById('llm-stuck-count');
            if (transcriptionStuckCount) transcriptionStuckCount.textContent = stats.transcription.processing;
            if (llmStuckCount && stats.llm) llmStuckCount.textContent = stats.llm.processing;

            // Update LLM issues panel counts
            const llmFailedCount = document.getElementById('llm-failed-count');
            const llmProcessingCount = document.getElementById('llm-processing-count');
            if (llmFailedCount && stats.llm) llmFailedCount.textContent = stats.llm.failed;
            if (llmProcessingCount && stats.llm) llmProcessingCount.textContent = stats.llm.processing;

            const dashboardStorage = document.getElementById('dashboard-storage');
            const dashboardAnnotated = document.getElementById('dashboard-annotated');
            const dashboardPodcasts = document.getElementById('dashboard-podcasts');

            if (dashboardStorage) dashboardStorage.textContent = stats.storage.total_gb + ' GB';
            if (dashboardAnnotated) dashboardAnnotated.textContent = stats.annotations.annotated;
            if (dashboardPodcasts) dashboardPodcasts.textContent = stats.podcasts;
        }
    } catch (error) {
        // Rethrow so startPoller handles logging with backoff
        throw error;
    }
}
